import AddIcon from '@mui/icons-material/Add';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Badge,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import MyModal from 'components/MyModal';
import MyTooltip from 'components/MyTooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import notification from 'services/notification';
import useCompanyBranch from './useCompanyBranch';

const iconButtonSx = {
  width: 40,
  height: 40,
  borderRadius: 1.25,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.primary',
  bgcolor: 'background.paper',
  '&:hover': {
    bgcolor: 'action.hover'
  }
};

function getBranchLimitMessage(error, t) {
  const raw = error?.response?.data?.data?.branch_limit?.[0];
  if (!raw) {
    return null;
  }

  const match = String(raw).match(/Limit:\s*(\d+).*?(?:filiallar|филиаллар):\s*(\d+)/i);
  if (match) {
    return t('Filial limiti tugagan', { limit: match[1], current: match[2] });
  }

  return t('Filial limiti tugagan oddiy');
}

export default function CompanyBranchHeader() {
  const { t } = useTranslation();
  const {
    companyId,
    companyName,
    branches,
    selectedBranch,
    selectedBranchId,
    switchBranch,
    createBranch
  } = useCompanyBranch();

  const [branchAnchor, setBranchAnchor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [branchName, setBranchName] = useState('');

  const selectedBranchIndex = branches.findIndex(
    (branch) => String(branch.id) === String(selectedBranchId)
  );
  const selectedBranchNumber = selectedBranchIndex >= 0 ? selectedBranchIndex + 1 : null;

  const closeAddModal = () => {
    setAddOpen(false);
    setBranchName('');
  };

  const handleCreateBranch = () => {
    if (!companyId || !branchName.trim()) {
      return;
    }

    createBranch.mutate(
      {
        name: branchName.trim(),
        company: companyId,
        description: null
      },
      {
        onSuccess: () => {
          closeAddModal();
          setBranchAnchor(null);
          notification.setMode('success').setMessage(t("Muvaffaqiyatli qo'shildi !")).success();
        },
        onError: (error) => {
          const limitMessage = getBranchLimitMessage(error, t);
          notification
            .setMode('error')
            .setMessage(limitMessage || t('Xatolik yuz berdi !'))
            .setAutoClose(limitMessage ? 5000 : 2000)
            .error();
        }
      }
    );
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          title={companyName || ''}
          sx={{
            maxWidth: { xs: 110, sm: 180, md: 240 },
            color: 'text.primary'
          }}>
          {t('Kompaniya')}: {companyName || '—'}
        </Typography>

        <MyTooltip title={selectedBranch?.name || t('Filial')} placement="bottom">
          <IconButton
            aria-label="branch"
            onClick={(event) => setBranchAnchor(event.currentTarget)}
            sx={iconButtonSx}>
            <Badge
              badgeContent={selectedBranchNumber}
              color="primary"
              overlap="circular"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: 10,
                  minWidth: 16,
                  height: 16,
                  top: -2,
                  right: -2
                }
              }}>
              <StorefrontIcon fontSize="small" />
            </Badge>
          </IconButton>
        </MyTooltip>
      </Stack>

      <Popover
        open={Boolean(branchAnchor)}
        anchorEl={branchAnchor}
        onClose={() => setBranchAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1.5, py: 1, minWidth: 220, maxWidth: 300 } }}>
        {branches.map((branch) => {
          const isSelected = String(branch.id) === String(selectedBranchId);
          return (
            <MenuItem
              key={branch.id}
              selected={isSelected}
              onClick={() => {
                switchBranch(branch);
                setBranchAnchor(null);
              }}
              sx={{
                mx: 1,
                borderRadius: 1,
                ...(isSelected && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }
                })
              }}>
              <ListItemText primary={branch.name} primaryTypographyProps={{ noWrap: true }} />
            </MenuItem>
          );
        })}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            setBranchAnchor(null);
            setAddOpen(true);
          }}
          disabled={!companyId}
          sx={{ mx: 1, borderRadius: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('Filial qoshish')} />
        </MenuItem>
      </Popover>

      <MyModal
        type="form"
        open={addOpen}
        handleClose={closeAddModal}
        withActionButtons
        cancel={t('Bekor qilish')}
        handleCancel={closeAddModal}
        ok={t("Qo'shish")}
        handleOk={handleCreateBranch}
        actionLoading={createBranch.isLoading}
        okProps={{ disabled: !branchName.trim() || createBranch.isLoading }}
        bodySx={{
          width: { xs: 'calc(100vw - 32px)', sm: 420 },
          maxWidth: 420,
          p: 3,
          minHeight: 'auto'
        }}>
        <Stack spacing={2.5} width="100%" alignItems="stretch">
          <Typography variant="h6" fontWeight={700} textAlign="left">
            {t('Filial qoshish')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Filial qoshish tavsif')}
          </Typography>
          <TextField
            label={t('Filial nomi')}
            value={branchName}
            onChange={(event) => setBranchName(event.target.value)}
            fullWidth
            required
            autoFocus
            size="small"
            placeholder={t('Filial nomi placeholder')}
          />
        </Stack>
      </MyModal>
    </>
  );
}
